import React from 'react';
import styled from '@benzinga/themetron';
import { DateTime } from 'luxon';
import { getValueBgClass, getPureNumber, formatLongPrice } from '@benzinga/calendars';
import { Economics } from '@benzinga/calendar-manager';

export const EconomicsColumnsDef = [
  {
    field: 'time',
    headerName: 'Time',
    valueFormatter: ({ data }) => {
      const result = DateTime.fromISO(data.time).toLocaleString(DateTime.TIME_SIMPLE);
      return result;
    },
  },
  {
    cellRenderer: ({ data }: { data: Economics }) => {
      return (
        <>
          <StyledCountryFlagIcon className="country-flag-icon" code={data.country} />
          {data.country}
        </>
      );
    },
    field: 'country',
    headerName: 'Country',
  },
  {
    cellRenderer: ({ data }: { data: Economics; index: number }) => {
      const importanceLevelPercentage = 100 / (5 / Number(data.importance)) || 0;
      return (
        <div className="flex items-center justify-center pr-2 w-[84px]">
          <StyledImpIndicator
            className="importance-indicator flex items-center w-full bg-blue-100 rounded h-5"
            level={importanceLevelPercentage}
            role="presentation"
          >
            <div
              className={`importance-indicator-value bg-blue-600 h-[18px] rounded text-white flex items-end justify-end pr-1`}
            >
              <span className="ml-2 inline-block">{data.importance}</span>
            </div>
          </StyledImpIndicator>
        </div>
      );
    },
    colSpan: 1,
    field: 'importance',
    headerName: 'Imp.',
  },
  {
    cellClass: 'semibold',
    colSpan: 4,
    field: 'event_name',
    headerName: 'Event',
  },
  {
    cellClass: ({ data }: { data: Economics }) => getValueBgClass(data.actual),
    field: 'actual',
    headerName: 'Actual',
    valueFormatter: ({ data }: { data: Economics }) => {
      const value = getPureNumber(data.actual);
      const formattedNumber = formatLongPrice(value, '');
      return formattedNumber;
    },
  },
  {
    cellClass: ({ data }: { data: Economics }) => getValueBgClass(data.consensus),
    field: 'consensus',
    headerName: 'Forecast',
    valueFormatter: ({ data }: { data: Economics }) => {
      const value = getPureNumber(data.consensus);
      const formattedNumber = formatLongPrice(value, '');
      return formattedNumber;
    },
  },
  {
    cellClass: ({ data }: { data: Economics }) => getValueBgClass(data.prior),
    field: 'prior',
    headerName: 'Previous',
    valueFormatter: ({ data }: { data: Economics }) => {
      const value = getPureNumber(data.prior);
      const formattedNumber = formatLongPrice(value, '');
      return formattedNumber;
    },
  },
];

export default EconomicsColumnsDef;

const StyledCountryFlagIcon = styled.div<{ code: string }>`
  &.country-flag-icon {
    margin-right: 0.3125rem;
    background-color: #fff;
    background: ${({ code }) =>
        `url(https://www.benzinga.com/wp-content/themes/benzinga-money/dist/rebuild/images/icons/countries-flags/alpha3/${code}.svg)`}
      no-repeat;
    background-size: cover;
    border: 1px solid #d6d6d6;
    display: inline-block;
    vertical-align: middle;
    width: 17px;
    height: 12px;
  }
`;

const StyledImpIndicator = styled.span<{ level: number }>`
  border: 1px solid ${({ theme }) => theme.colorPalette.blue200};
  .importance-indicator-value {
    background: linear-gradient(270deg, #1a79ff 0%, #97c0f9 100%);
    width: ${({ level }) => `${level}%`};
  }
`;
